import React, { FC, useEffect, useState } from "react";
import { LuPlus, LuSearch, LuX } from "react-icons/lu";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import ConfirmModal from "../../../../components/modal/ConfirmModal";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { Button as NewButton } from "../../../../components/newUI/button";
import { Input } from "../../../../components/newUI/input";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import {
  useDeleteParticipantsMutation,
  useDeleteWorkNoteMutation,
  useGetParticipantsQuery,
  useGetWorkNotesQuery,
} from "../../../../slices/WorkNotesSlice";
import { IUser, IWorkNote } from "../../../../types/types";
import {
  getNoteAuthorColor,
  getNoteAuthorName,
  NoteEmptyState,
  useCanEditNote,
} from "../../../contacts/components/notes";
import { NoteViewModal } from "../../../contacts/components/NoteViewModal";
import { WorkNoteModal } from "../../modals/WorkNoteModal";
import { WorkNote } from "./WorkNote";
import { visibleWorkNotes } from "./workNotes.utils";

export const WorkNotes: FC = () => {
  const { workId } = useParams();

  const { data: workNotes, isLoading } = useGetWorkNotesQuery({
    workId: workId || "",
  });

  const { data: participants, isLoading: isLoadingParticipants } =
    useGetParticipantsQuery({
      workId: workId || "",
    });

  const [
    deleteParticipant,
    { isSuccess, isLoading: isLoadingDeleteParticipant },
  ] = useDeleteParticipantsMutation();
  const [deleteWorkNote, { isLoading: isDeletingNote }] =
    useDeleteWorkNoteMutation();
  // Deleting a note asks first (Roadmap 1807).
  const [noteToDelete, setNoteToDelete] = useState<IWorkNote | null>(null);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [currentNote, setCurrentNote] = useState<IWorkNote | null>(null);
  const [viewNote, setViewNote] = useState<IWorkNote | null>(null);
  const canEdit = useCanEditNote();
  const [search, setSearch] = useState("");
  const [confirmDeleteParticipant, setConfirmDeleteParticipant] = useState<{
    id: string;
    userId: string;
    user: IUser;
  } | null>(null);

  const handleOpenAddModal = () => {
    setIsOpenAdd(true);
    setCurrentNote(null);
  };

  useEffect(() => {
    if (isSuccess) {
      setConfirmDeleteParticipant(null);
    }
  }, [isSuccess]);

  const notes = visibleWorkNotes(workNotes, search);
  const count = workNotes?.length || 0;

  return (
    <>
      <div className={"w-full max-w-[860px] py-5"}>
        <div className={"mb-4 flex w-full items-center justify-between gap-4"}>
          <span className={"text-[13px] font-medium text-[#9097A8]"}>
            {count} {count === 1 ? "note" : "notes"}
          </span>
          <NewButton
            type={"button"}
            variant={"primary"}
            size={"md"}
            autoWidth
            leftIcon={<LuPlus size={16} strokeWidth={2.2} />}
            className={"h-10 px-[18px] text-[13.5px] font-bold"}
            onClick={handleOpenAddModal}>
            Add note
          </NewButton>
        </div>

        {(count > 0 || !!search) && (
          <div className={"relative mb-4"}>
            <Input
              value={search}
              placeholder={"Search notes"}
              leftIcon={<LuSearch size={16} strokeWidth={2} />}
              className={"h-[42px] rounded-[9px] text-[13.5px]"}
              onChange={(e) => setSearch(e.target.value)}
              rightIcon={
                search ? (
                  <NewButton
                    type={"button"}
                    variant={"ghost"}
                    size={"xs"}
                    iconOnly
                    autoWidth
                    leftIcon={<LuX size={16} />}
                    className={"text-[#A6ACBB] hover:text-[#6B7185]"}
                    onClick={() => setSearch("")}
                  />
                ) : null
              }
            />
          </div>
        )}

        {isLoading ? (
          <div className={"p-4"}>
            <LottieLoading />
          </div>
        ) : !notes.length ? (
          <NoteEmptyState
            variant={search ? "noResults" : "empty"}
            emptyDescription={"Notes added to this work item will appear here."}
          />
        ) : (
          <div className={"flex flex-col gap-3"}>
            {notes.map((note) => (
              <WorkNote
                key={note.id}
                note={note}
                onView={setViewNote}
                onEdit={(item) => {
                  setCurrentNote(item);
                  setIsOpenAdd(true);
                }}
                onDelete={setNoteToDelete}
              />
            ))}
          </div>
        )}
      </div>
      {viewNote && (
        <NoteViewModal
          note={{
            id: viewNote.id,
            title: viewNote.title,
            note: viewNote.note,
            createdAt: viewNote.createdAt,
            authorName: getNoteAuthorName(viewNote),
            authorColor: getNoteAuthorColor(viewNote),
          }}
          onClose={() => setViewNote(null)}
          onEdit={
            !canEdit(viewNote)
              ? undefined
              : () => {
                  setCurrentNote(viewNote);
                  setViewNote(null);
                  setIsOpenAdd(true);
                }
          }
        />
      )}
      {noteToDelete && (
        <SettingsConfirmModal
          onClose={() => setNoteToDelete(null)}
          onConfirm={async () => {
            try {
              await deleteWorkNote(noteToDelete).unwrap();
            } catch {
              toast.error("Failed to delete the note");
            } finally {
              setNoteToDelete(null);
            }
          }}
          isLoading={isDeletingNote}
          icon={<TrashSvg className={"h-5 w-5"} />}
          title={"Delete this note?"}
          description={"Do you want to permanently delete this note?"}
          confirmText={"Delete"}
          variant={"danger"}
        />
      )}
      {confirmDeleteParticipant && (
        <ConfirmModal
          confirm={() =>
            deleteParticipant({
              workId: workId || "",
              id: confirmDeleteParticipant?.id || "",
            })
          }
          close={() => setConfirmDeleteParticipant(null)}
          title={""}
          message={
            <div className={"text-base text-gray-800"}>
              This user will no longer receive notifications regarding this
              conversation. Notifications can be resumed by using the @mention
              feature.
            </div>
          }
          isLoading={isLoadingDeleteParticipant}
          buttonsClasses={"flex-row-reverse gap-4"}
        />
      )}
      {isOpenAdd && (
        <WorkNoteModal
          workId={workId || ""}
          participants={participants || []}
          onClose={() => {
            setIsOpenAdd(false);
            setCurrentNote(null);
          }}
          isActive={isOpenAdd}
          defaultValues={currentNote || undefined}
        />
      )}
    </>
  );
};
